import React from 'react';
import { Box, Form, TextArea, Button, FormExtendedEvent, Stack, Text } from 'grommet';
import { Send } from 'grommet-icons';
import { LoadingDots } from '../LoadingDots';

export interface IMentionCandidate {
  id: string;
  label: string;
}

export interface IUserInputProps {
  handleSubmit: (e: FormExtendedEvent) => void;
  loading: boolean;
  handleEnter: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  query: string;
  setQuery: (query: string) => void;
  gridArea?: string;
  mentionCandidates?: IMentionCandidate[];
}

// Only recognizes an @-mention actively being typed at the end of the current text —
// a deliberate simplification over full caret-position tracking (not needed for a
// single-line-ish chat composer).
const MENTION_PATTERN = /(?:^|\s)@([a-zA-Z0-9._-]*)$/;

export const UserInput = ({
  handleSubmit,
  loading,
  handleEnter,
  textAreaRef,
  query,
  setQuery,
  gridArea,
  mentionCandidates = [],
}: IUserInputProps) => {
  const mentionMatch = MENTION_PATTERN.exec(query);
  const activeQuery = mentionMatch?.[1];
  const suggestions =
    activeQuery !== undefined && mentionCandidates.length
      ? mentionCandidates
        .filter((c) => c.label.toLowerCase().startsWith(activeQuery.toLowerCase()))
        .slice(0, 6)
      : [];

  const selectMention = (candidate: IMentionCandidate) => {
    const match = MENTION_PATTERN.exec(query);
    if (!match) return;
    const leadingChar = match[0][0] === '@' ? '' : match[0][0];
    const prefix = query.slice(0, match.index) + leadingChar;
    setQuery(`${prefix}@[${candidate.label}](${candidate.id}) `);
    textAreaRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      selectMention(suggestions[0]);
      return;
    }
    handleEnter(e);
  };

  return (
    <Box gridArea={gridArea} fill="horizontal" pad="small" style={{ position: 'relative' }}>
      {suggestions.length > 0 && (
        <Box
          background="white"
          border={{ color: '#d9d9e3' }}
          round="small"
          elevation="small"
          style={{ position: 'absolute', bottom: '100%', left: '12px', right: '12px', zIndex: 20 }}
        >
          {suggestions.map((s) => (
            <Box
              key={s.id}
              pad={{ vertical: 'xsmall', horizontal: 'small' }}
              hoverIndicator
              style={{ cursor: 'pointer' }}
              onClick={() => selectMention(s)}
            >
              <Text size="small">@{s.label}</Text>
            </Box>
          ))}
        </Box>
      )}
      <Form onSubmit={handleSubmit}>
        <Box style={{ position: 'relative' }}>
          <Stack anchor="right">
            <TextArea
              disabled={loading}
              onKeyDown={onKeyDown}
              ref={textAreaRef}
              autoFocus={false}
              focusIndicator={false}
              rows={1}
              maxLength={512}
              id="userInput"
              name="userInput"
              placeholder={loading ? 'Waiting for response...' : 'Enter your question here...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fill
            />
            <Box pad={{ horizontal: 'small' }}>
              <Button type="submit" disabled={loading || !query}>
                {loading ? (
                  <div>
                    <LoadingDots color="#000" />
                  </div>
                ) : (
                  <Send />
                )}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Form>
    </Box>
  );
};
